import { useCallback, useEffect, useRef, useState } from "react";
import { customFieldsRequest } from "./api";

/** Identity changes hide old data immediately. Opt-in catalog reloads and focus checks
 * keep the mounted view until the response replaces it; failures discard metadata.
 */
const useCustomFieldsResource = ({
  teamId,
  path,
  generation = 0,
  enabled = true,
  retainOnFocus = false,
  retainOnReload = false,
  retentionKey = path,
  revalidateOnFocus = true,
  handleUnavailable = false,
}) => {
  const [focusGeneration, setFocusGeneration] = useState(0);
  const refreshing = useRef(false);
  const [reloadGeneration, setReloadGeneration] = useState(0);
  const identity = JSON.stringify([
    teamId,
    path,
    generation,
    reloadGeneration,
    enabled,
  ]);
  const currentIdentity = useRef(identity);
  const loadedIdentity = useRef(null);
  const scope = JSON.stringify([teamId, retentionKey, enabled]);
  currentIdentity.current = identity;
  const [state, setState] = useState({
    identity: null,
    data: null,
    error: null,
    loading: true,
  });
  const reload = useCallback(
    () => setReloadGeneration((value) => value + 1),
    [],
  );

  useEffect(() => {
    const revalidate = () => {
      if (
        !enabled ||
        !revalidateOnFocus ||
        teamId == null ||
        refreshing.current
      ) {
        return;
      }
      refreshing.current = true;
      if (retainOnFocus) {
        setFocusGeneration((value) => value + 1);
      } else {
        reload();
      }
    };
    const visibility = () => {
      if (document.visibilityState === "visible") {
        revalidate();
      }
    };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [reload, retainOnFocus, revalidateOnFocus, enabled, teamId]);

  useEffect(() => {
    if (!enabled || teamId == null) {
      loadedIdentity.current = null;
      setState({ identity: null, data: null, error: null, loading: true });
      return undefined;
    }
    // Pausing cancels an outstanding focus check without replacing an edit snapshot.
    // New identities and explicit reloads still fetch; resuming refreshes read mode.
    if (!revalidateOnFocus && loadedIdentity.current === identity) {
      return undefined;
    }
    if (loadedIdentity.current !== identity) {
      loadedIdentity.current = null;
    }
    refreshing.current = true;
    const controller = new AbortController();
    let active = true;
    customFieldsRequest({
      teamId,
      path,
      signal: controller.signal,
      handleUnavailable,
    }).then(
      (data) => {
        if (active && currentIdentity.current === identity) {
          refreshing.current = false;
          loadedIdentity.current = identity;
          setState({ identity, scope, data, error: null, loading: false });
        }
      },
      (error) => {
        if (active && currentIdentity.current === identity) {
          refreshing.current = false;
          loadedIdentity.current = null;
          setState({ identity, scope, data: null, error, loading: false });
        }
      },
    );
    return () => {
      refreshing.current = false;
      active = false;
      controller.abort();
    };
  }, [
    identity,
    scope,
    enabled,
    teamId,
    path,
    focusGeneration,
    revalidateOnFocus,
    handleUnavailable,
  ]);

  const visible =
    enabled &&
    teamId != null &&
    (state.identity === identity || (retainOnReload && state.scope === scope));
  return {
    data: visible ? state.data : null,
    error: visible ? state.error : null,
    loading: enabled && teamId != null && (!visible || state.loading),
    refreshing: enabled && teamId != null && state.identity !== identity,
    reload,
  };
};

export default useCustomFieldsResource;
